import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Form,
  Input,
  Select,
  Button,
  Upload,
  Typography,
  Card,
  Space,
  Divider,
  message,
  Spin,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import { UploadOutlined } from "@ant-design/icons";
import logo from "../assets/logo/main-logo.png";
import { publicDonationUploadFile } from "../views/common/sharedStorageApi";
import { useQuery } from "@tanstack/react-query";
import {
  createTrustDonation,
  getSubCategoryForDonationForm,
} from "../utility/commonApi";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getDetails } from "../apis/getDetails";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

const labelStyle = {
  fontFamily: "'Noto Sans', sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#533810",
  marginBottom: 6,
};

const inputStyle = {
  fontFamily: "'Noto Sans', sans-serif",
  color: "#533810",
};

const cardSectionStyle = {
  background: "#fff",
  border: "1px solid #f0f0f0",
  borderRadius: 12,
  padding: "20px 24px",
  marginBottom: 24,
  boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
};

const PublicDonationForm = () => {
  const trustId = localStorage.getItem("trustId");
  const [form] = Form.useForm();
  const userDetails = useSelector((state) => state.auth);
  const selectedLang = useSelector((state) => state.auth.selectLang);
  const trustData = userDetails?.trustData;

  const subDomain = window.location.host.replace(
    process.env.REACT_APP_SUBDOMAIN_REPLACE_VALUE,
    ""
  );
  const location = useLocation();

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ["details"],
    queryFn: () => getDetails(subDomain),
  });

  const trustDetails = useMemo(() => data?.data?.result ?? null, [data]);
  useEffect(() => {
    if (trustDetails) {
      localStorage.setItem("trustId", trustDetails.id);
      localStorage.setItem("trustData", JSON.stringify(trustDetails));
    }
  }, [trustDetails]);
  const finalTrustId = trustData?.id ?? trustDetails?.id ?? trustId;

  const { data: categoryData, isLoading } = useQuery({
    queryKey: ["categories", selectedLang, finalTrustId],
    queryFn: () => getSubCategoryForDonationForm(finalTrustId, selectedLang),
    keepPreviousData: true,
    enabled: !!finalTrustId, // avoid running if no trustId available
  });

  // Memoizing the fetched data
  const categoryList = useMemo(
    () => categoryData?.data?.results ?? [],
    [categoryData]
  );

  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false); // to control UI

  const navigate = useNavigate();
  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      trustId: trustData?.id ?? trustDetails?.id,
      paymentMethod: "Bank Transfer",
      countryCode: "+91",
      countryName: "India",
    };
    try {
      setLoading(true);

      const res = await createTrustDonation(payload);

      if (res?.error) {
        Swal.fire("Error", "Something went wrong. Please try again.", "error");
      } else {
        Swal.fire(
          "Success",
          "Your donation was submitted successfully.",
          "success"
        );
        setSubmitted(true); // hide form, show thank-you
      }
    } catch (error) {
      Swal.fire(
        "Error",
        "Something went wrong. Please try again later.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };
  const ThankYouMessage = () => (
    <div
      style={{
        height: "100vh", // Full viewport height
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
        padding: "1rem",
      }}
    >
      <div
        style={{
          backgroundColor: "#f6ffed",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          padding: "3rem 2rem",
          maxWidth: "500px",
          width: "100%",
          textAlign: "center",
        }}
      >
        <h2
          style={{ fontSize: "28px", color: "#52c41a", marginBottom: "1rem" }}
        >
          🎉 Thank You!
        </h2>
        <p style={{ fontSize: "16px", color: "#333", marginBottom: "2rem" }}>
          We sincerely appreciate your generous contribution.
        </p>
        <Button
          type="primary"
          // size="small"
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: "#FF8744",
            borderColor: "#FF8744",
            fontFamily: "'Noto Sans', sans-serif",
            fontWeight: 600,
            padding: "6px 24px",
            borderRadius: 6,
          }}
        >
          Go Back to Donation Form
        </Button>
      </div>
    </div>
  );
  if (isFetching) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: 16,
          backgroundColor: "#f0f2f5",
        }}
      >
        <Card
          style={{
            padding: "40px 30px",
            borderRadius: 12,
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Spin size="large" />
          <p style={{ marginTop: 16 }} className="commonSmallFont">
            Loading trust details...
          </p>
        </Card>
      </div>
    );
  }

  if (!trustData && !trustDetails) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: 16,
          backgroundColor: "#f0f2f5",
        }}
      >
        <Card
          style={{
            padding: "40px 30px",
            borderRadius: 12,
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <p className="commonSmallFont" style={{ marginBottom: 24 }}>
            Unable to load trust details. Please try again.
          </p>
          <Button
            type="primary"
            style={{
              backgroundColor: "#ff8744",
              borderColor: "#ff8744",
              fontWeight: 600,
            }}
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </Card>
      </div>
    );
  }
  return (
    <>
      <style>
        {`
        @media (max-width: 768px) {
          .responsive-header-card-container {
            flex-direction: column !important;
            text-align: center !important;
            gap: 15px !important;
          }
          
          .responsive-header-card-content {
            text-align: center !important;
          }
          
          .responsive-header-card-image {
            width: 120px !important;
            height: 120px !important;
          }
          
          .responsive-header-card-title {
            font-size: 20px !important;
          }
          
          .responsive-header-card-text {
            font-size: 14px !important;
          }
        }
        
        @media (max-width: 480px) {
          .responsive-header-card-image {
            width: 100px !important;
            height: 100px !important;
          }
          
          .responsive-header-card-title {
            font-size: 18px !important;
          }
          
          .responsive-header-card-text {
            font-size: 13px !important;
          }
        }
      `}
      </style>
      {!submitted ? (
        <div
          style={{
            maxWidth: 650,
            margin: "0 auto",
            padding: "24px 16px",
            backgroundColor: "#fafafa",
            fontFamily: "'Noto Sans', sans-serif",
            color: "#533810",
          }}
        >
          {/* Header Card */}
          <Card
            bordered={false}
            style={{
              backgroundColor: "#FF8744",
              borderRadius: 10,
              margin: "0 auto 24px auto",
              padding: "16px 24px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
              }}
              className="responsive-header-card-container"
            >
              {/* Circular Image */}
              <img
                src={trustData?.profilePhoto ?? trustDetails?.profilePhoto}
                alt={(trustData?.name ?? trustDetails?.name) || "Trust Profile"}
                style={{
                  width: 150,
                  height: 150,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "3px solid rgba(255, 255, 255, 0.8)",
                  flexShrink: 0,
                }}
                className="responsive-header-card-image"
              />

              {/* Content */}
              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                }}
                className="responsive-header-card-content"
              >
                <Title
                  level={2}
                  style={{
                    color: "#fff",
                    margin: "0 0 8px 0",
                    fontSize: 24,
                  }}
                  className="responsive-header-card-title"
                >
                  {(trustData?.name ?? trustDetails?.name) || "Trust Name"}
                </Title>
                <Text
                  style={{
                    color: "#fff",
                    fontSize: 16,
                    opacity: 0.9,
                  }}
                  className="responsive-header-card-text"
                >
                  {(trustData?.address ?? trustDetails?.address) ||
                    "Trust Address"}
                </Text>
              </div>
            </div>
          </Card>

          {/* Form Title */}

          {/* Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            requiredMark={false}
            initialValues={{
              createdAt: dayjs(), // set current date as default
            }}
          >
            {/* Personal Info */}
            <div style={cardSectionStyle}>
              <Title
                level={4}
                style={{
                  fontWeight: 700,
                  textAlign: "center",
                  color: "#533810",
                  fontFamily: "Noto Sans",
                  marginBottom: 24,
                }}
              >
                Donation Information Form
              </Title>
              <Form.Item
                label={
                  <div style={labelStyle}>
                    Full Name<span className="text-danger">*</span>
                  </div>
                }
                name="donarName"
                rules={[{ required: true, message: "Please enter your name" }]}
              >
                <Input placeholder="Enter your full name" style={inputStyle} />
              </Form.Item>

              <Form.Item
                label={
                  <div style={labelStyle}>
                    Mobile Number<span className="text-danger">*</span>
                  </div>
                }
                name="mobileNumber"
                rules={[
                  {
                    required: true,
                    message: "Please enter your mobile number",
                  },
                  {
                    pattern: /^\d{10}$/,
                    message: "Enter a valid 10-digit number",
                  },
                ]}
              >
                <Input
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                  style={inputStyle}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label={<div style={labelStyle}>Email Address (Optional)</div>}
                name="email"
                rules={[
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input
                  placeholder="Enter your email"
                  style={inputStyle}
                  onKeyPress={(e) => {
                    const allowedChars = /^[a-zA-Z0-9@._-]+$/;
                    if (!allowedChars.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                label={
                  <div style={labelStyle}>
                    Donation Date<span className="text-danger">*</span>
                  </div>
                }
                name="createdAt"
                rules={[
                  { required: true, message: "Please select donation date" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%", ...inputStyle }}
                  format="DD MMM YYYY"
                  onChange={(date) => form.setFieldsValue({ createdAt: date })}
                  disabledDate={(current) => {
                    // Disable future dates
                    return current && current > moment().endOf("day");
                  }}
                />
              </Form.Item>
              <Form.Item
                label={
                  <div style={labelStyle}>
                    Donation For<span className="text-danger">*</span>
                  </div>
                }
                name="categoryId"
                rules={[
                  {
                    required: true,
                    message: "Please select a donation category",
                  },
                ]}
              >
                <Select placeholder="Select donation category">
                  {categoryList.map((item) => (
                    <Option key={item._id} value={item._id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={
                  <div style={labelStyle}>
                    Donation Amount<span className="text-danger">*</span>
                  </div>
                }
                name="amount"
                rules={[{ required: true, message: "Please enter the amount" }]}
              >
                <Input
                  type="number"
                  min={0}
                  placeholder="Enter donation amount"
                  style={inputStyle}
                  onKeyPress={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.target.blur()} // Prevent scroll on input change
                />
              </Form.Item>

              <Form.Item
                label={
                  <div style={labelStyle}>
                    Upload Receipt/Image
                    <span className="text-danger">*</span>
                  </div>
                }
                name="paymentScreenShot"
                // We store only the filePath from response, not the Upload file object
                rules={[
                  {
                    required: true,
                    message: "Please upload a receipt or image",
                  },
                ]}
                getValueFromEvent={() => null} // prevent Upload from setting fileList
              >
                <Upload
                  customRequest={async ({ file, onSuccess, onError }) => {
                    setUploading(true);
                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("trustId", trustId);
                    formData.append("path", `publicDonation/${file?.name}`);
                    try {
                      const res = await publicDonationUploadFile(
                        formData,
                        false
                      ); // false = single upload
                      if (res?.data?.result?.filePath) {
                        form.setFieldsValue({
                          paymentScreenShot: res.data.result.filePath,
                        });
                        message.success("File uploaded successfully");
                        onSuccess("ok"); // Tell Upload it's done
                      } else {
                        message.error("Failed to get uploaded file path");
                        onError(new Error("No filePath in response"));
                      }
                    } catch (err) {
                      message.error("Upload failed");
                      onError(err);
                    } finally {
                      setUploading(false);
                    }
                  }}
                  // showUploadList={false} // Don't show default file list
                  listType="picture"
                  maxCount={1}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={uploading}
                    style={{
                      fontFamily: "'Noto Sans', sans-serif",
                      width: "100%",
                    }}
                  >
                    Click to Upload Payment Screenshot
                  </Button>
                </Upload>
              </Form.Item>

              <Form.Item
                label={<div style={labelStyle}>Remarks</div>}
                name="donationRemarks"
                rules={[{ message: "Remark for your donation" }]}
              >
                <Input.TextArea
                  placeholder="Enter remark if any"
                  maxLength={300} // limit to 300 characters
                  showCount
                  style={inputStyle}
                />
              </Form.Item>

              <Form.Item style={{ textAlign: "center" }}>
                <Space
                  style={{ width: "100%" }}
                  direction="vertical"
                  size="middle"
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{
                      backgroundColor: "#FF8744",
                      borderColor: "#FF8744",
                      fontFamily: "'Noto Sans', sans-serif",
                      fontWeight: 600,
                      padding: "6px 24px",
                      borderRadius: 6,
                      width: "100%",
                    }}
                    loading={loading}
                    disabled={loading}
                  >
                    Submit
                  </Button>
                  <Button
                    htmlType="reset"
                    onClick={() => form.resetFields()}
                    style={{
                      fontFamily: "'Noto Sans', sans-serif",
                      fontWeight: 600,
                      borderRadius: 6,
                      width: "100%",
                    }}
                  >
                    Reset
                  </Button>
                </Space>
                <div style={{ paddingTop: 20, textAlign: "center" }}>
                  <span
                    style={{
                      fontFamily: "'Noto Sans', sans-serif",
                      fontSize: 16,
                      marginRight: 8,
                    }}
                  >
                    Powered by
                  </span>
                  <img
                    src={logo}
                    alt="Powered by Logo"
                    style={{ height: 36, verticalAlign: "middle" }}
                  />
                </div>
              </Form.Item>
            </div>
          </Form>
        </div>
      ) : (
        <ThankYouMessage />
      )}
    </>
  );
};

export default PublicDonationForm;
